import { Container, Row, Col, Form, Button, Tabs, Tab } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { ProjectSlice, selectProject, updateGammaApi } from "../../features/projects/projectSlice";
import { SXEditField } from "@common/components/ui-components/SXEditField";
import { SXDropdown, type NGDropdownListItem } from "@common/components/ui-components/SXDropdown";
import { SXModalBase } from "./SXModal";
import { ng } from "@common/ngds";

interface MaterialSettingsModalProps {
  show: boolean;
  close: () => void;
  selectedGroup: "Concrete" | "Steel" | "Timber";
}

interface PartialValues {
  gammaC: number;
  gammaT: number;
  gammaReinf: number;
  gammaCe: number;
  alphaCc: number;
  alphaCt: number;
}

interface TimberPartialValues {
  gammaM: number;
  gammaMGl: number;
  gammaMLvl: number;
  gammaMPlate: number;
}

interface SteelPartialValues {
  gammaM0: number;
  gammaM1: number;
  gammaM2: number;
  gammaM5: number;
}

const DEFAULT_PARTIAL_VALUE = 1.0;

interface RawPartialCoefficients {
  gammaCompression?: number | string;
  gammaTension?: number | string;
  gammaReinforcement?: number | string;
  gammaCe?: number | string;
  alfaCc?: number | string;
  alfaCt?: number | string;
}

const toFiniteNumber = (value?: number | string): number => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : DEFAULT_PARTIAL_VALUE;
  }

  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : DEFAULT_PARTIAL_VALUE;
  }

  return DEFAULT_PARTIAL_VALUE;
};

const mapPartialValues = (values?: RawPartialCoefficients | null): PartialValues => ({
  gammaC: toFiniteNumber(values?.gammaCompression),
  gammaT: toFiniteNumber(values?.gammaTension),
  gammaReinf: toFiniteNumber(values?.gammaReinforcement),
  gammaCe: toFiniteNumber(values?.gammaCe),
  alphaCc: toFiniteNumber(values?.alfaCc),
  alphaCt: toFiniteNumber(values?.alfaCt),
});


const FCTM_TYPE_OPTIONS: Array<{ value: string; label: string }> =
  [
    { value: "FCTM_TYPE_FCTM", label: "fctm" },
    { value: "FCTM_TYPE_FCTM_FL", label: "fctm·fl" },
  ];

const SHEAR_DESIGN_TYPE_OPTIONS: Array<NGDropdownListItem<string>> = [
  { id: "SHEAR_DESIGN_TYPE_WITH_SHEAR_REINFORCEMENT", name: "With shear reinforcement" },
  { id: "SHEAR_DESIGN_TYPE_WITHOUT_SHEAR_REINFORCEMENT", name: "Without shear reinforcement" },
];

const FCTM_TYPE_DROPDOWN_OPTIONS: Array<NGDropdownListItem<string>> = FCTM_TYPE_OPTIONS.map((option) => ({
  id: option.value,
  name: option.label,
}));

const renderPartialRow = (labelHtml: string, value: number) => (
  <Row className="align-items-center mb-2 gx-0">
    <Col xs={6} className="editfield-label mb-0 ps-3">
      <span
        style={{ fontSize: 16, color: "var(--text-muted, #808080)" }}
        dangerouslySetInnerHTML={{ __html: labelHtml }}
      />
    </Col>
    <Col xs={6} className="ps-0 pe-1" style={{ height: 32 }}>
      <SXEditField fullWidth textAlign="left" fontSize={12} type="number" disableNegativeNumber val={value} disabled />
    </Col>
  </Row>
);

const PartialCoefficientsTabContent = ({ values }: { values: PartialValues }) => (
  <Container fluid className="px-2 py-2 border rounded">
    {renderPartialRow("&gamma;<sub>c</sub>", values.gammaC)}
    {renderPartialRow("&gamma;<sub>t</sub>", values.gammaT)}
    {renderPartialRow("&gamma;<sub>reinf</sub>", values.gammaReinf)}
    {renderPartialRow("&gamma;<sub>ce</sub>", values.gammaCe)}
    {renderPartialRow("&alpha;<sub>cc</sub>", values.alphaCc)}
    {renderPartialRow("&alpha;<sub>ct</sub>", values.alphaCt)}
  </Container>
);

const TimberPartialTabContent = ({ values }: { values: TimberPartialValues }) => (
  <Container fluid className="px-2 py-2 border rounded">
    {renderPartialRow("&gamma;<sub>m</sub>", values.gammaM)}
    {renderPartialRow("&gamma;<sub>m,gl</sub>", values.gammaMGl)}
    {renderPartialRow("&gamma;<sub>m,lvl</sub>", values.gammaMLvl)}
    {renderPartialRow("&gamma;<sub>m,plate</sub>", values.gammaMPlate)}
  </Container>
);

const SteelPartialTabContent = ({ values }: { values: SteelPartialValues }) => (
  <Container fluid className="px-2 py-2 border rounded">
    {renderPartialRow("&gamma;<sub>m0</sub>", values.gammaM0)}
    {renderPartialRow("&gamma;<sub>m1</sub>", values.gammaM1)}
    {renderPartialRow("&gamma;<sub>m2</sub>", values.gammaM2)}
    {renderPartialRow("&gamma;<sub>m5</sub>", values.gammaM5)}
  </Container>
);

const PartialCoefficientsTabs = () => {
  const { t } = useTranslation();
  const selProject = useAppSelector(selectProject);
  const partialCoeffs =
    selProject?.data?.data?.input?.structures?.[0]?.elements?.[0]?.designSettings?.rc?.partialCoeffs ?? null;

  const ultimate = mapPartialValues(partialCoeffs?.ultimate);
  const service = mapPartialValues(partialCoeffs?.service);
  const accidental = mapPartialValues(partialCoeffs?.accidental);
  const fire = mapPartialValues(partialCoeffs?.fire);

  return (
    <Container fluid className="px-3">
      <Row className="mb-3">
        <Col>
          <h6 className="border-bottom pb-2">{t("partial_coefficients_.label", "Partial coefficients")}</h6>
        </Col>
      </Row>

      <Tabs defaultActiveKey="ultimate" className="mb-3">
        <Tab eventKey="ultimate" title={t("ultimate_.label", "Ultimate")}>
          <PartialCoefficientsTabContent values={ultimate} />
        </Tab>
        <Tab eventKey="accidental" title={t("accident_.label", "Accidental")}>
          <PartialCoefficientsTabContent values={accidental} />
        </Tab>
        <Tab eventKey="fire" title={t("fire_.label", "Fire")}>
          <PartialCoefficientsTabContent values={fire} />
        </Tab>
        <Tab eventKey="service" title={t("characteristic_.label", "Characteristic")}>
          <PartialCoefficientsTabContent values={service} />
        </Tab>
      </Tabs>
    </Container>
  );
};

const TimberPartialCoefficientsTabs = () => {
  const { t } = useTranslation();
  const selProject = useAppSelector(selectProject);
  const partialCoeffs = selProject?.data?.data?.input?.structures?.[0]?.designSettings?.timber?.partialCoeffs ?? null;

  const ultimate: TimberPartialValues = {
    gammaM: toFiniteNumber(partialCoeffs?.ultimate?.gammaM),
    gammaMGl: toFiniteNumber(partialCoeffs?.ultimate?.gammaMGl),
    gammaMLvl: toFiniteNumber(partialCoeffs?.ultimate?.gammaMLvl),
    gammaMPlate: toFiniteNumber(partialCoeffs?.ultimate?.gammaMPlate),
  };

  const accidental: TimberPartialValues = {
    gammaM: toFiniteNumber(partialCoeffs?.accidental?.gammaM),
    gammaMGl: toFiniteNumber(partialCoeffs?.accidental?.gammaMGl),
    gammaMLvl: toFiniteNumber(partialCoeffs?.accidental?.gammaMLvl),
    gammaMPlate: toFiniteNumber(partialCoeffs?.accidental?.gammaMPlate),
  };

  const gammaFire = toFiniteNumber(partialCoeffs?.gammaFire);

  return (
    <Container fluid className="px-3">
      <Row className="mb-3">
        <Col>
          <h6 className="border-bottom pb-2">{t("partial_coefficients_.label", "Partial coefficients")}</h6>
        </Col>
      </Row>

      <Tabs defaultActiveKey="ultimate" className="mb-3">
        <Tab eventKey="ultimate" title={t("ultimate_.label", "Ultimate")}>
          <TimberPartialTabContent values={ultimate} />
        </Tab>
        <Tab eventKey="accidental" title={t("accident_.label", "Accidental")}>
          <TimberPartialTabContent values={accidental} />
        </Tab>
        <Tab eventKey="fire" title={t("fire_.label", "Fire")}>
          <Container fluid className="px-2 py-2 border rounded">
            {renderPartialRow("&gamma;<sub>fire</sub>", gammaFire)}
          </Container>
        </Tab>
      </Tabs>
    </Container>
  );
};

const SteelPartialCoefficientsTabs = () => {
  const { t } = useTranslation();
  const selProject = useAppSelector(selectProject);
  const partialCoeffs = selProject?.data?.data?.input?.structures?.[0]?.designSettings?.steel?.partialCoeffs ?? null;

  const ultimate: SteelPartialValues = {
    gammaM0: toFiniteNumber(partialCoeffs?.ultimate?.gammaM0),
    gammaM1: toFiniteNumber(partialCoeffs?.ultimate?.gammaM1),
    gammaM2: toFiniteNumber(partialCoeffs?.ultimate?.gammaM2),
    gammaM5: toFiniteNumber(partialCoeffs?.ultimate?.gammaM5),
  };

  const accidental: SteelPartialValues = {
    gammaM0: toFiniteNumber(partialCoeffs?.accidental?.gammaM0),
    gammaM1: toFiniteNumber(partialCoeffs?.accidental?.gammaM1),
    gammaM2: toFiniteNumber(partialCoeffs?.accidental?.gammaM2),
    gammaM5: toFiniteNumber(partialCoeffs?.accidental?.gammaM5),
  };

  const gammaFire = toFiniteNumber(partialCoeffs?.gammaFire);

  return (
    <Container fluid className="px-3">
      <Row className="mb-3">
        <Col>
          <h6 className="border-bottom pb-2">{t("partial_coefficients_.label", "Partial coefficients")}</h6>
        </Col>
      </Row>

      <Tabs defaultActiveKey="ultimate" className="mb-3">
        <Tab eventKey="ultimate" title={t("ultimate_.label", "Ultimate")}>
          <SteelPartialTabContent values={ultimate} />
        </Tab>
        <Tab eventKey="accidental" title={t("accident_.label", "Accidental")}>
          <SteelPartialTabContent values={accidental} />
        </Tab>
        <Tab eventKey="fire" title={t("fire_.label", "Fire")}>
          <Container fluid className="px-2 py-2 border rounded">
            {renderPartialRow("&gamma;<sub>fire</sub>", gammaFire)}
          </Container>
        </Tab>
      </Tabs>
    </Container>
  );
};

// Inspection level and critical element live on the element and only apply to the Danish National
// Annex (EN 1990 Annex E NA), where they feed the partial factors. They are the same two settings
// for concrete, steel and timber - only the label's code reference differs, hence labelKey.
const DanishElementSettings = ({ labelKey, idPrefix }: { labelKey: string; idPrefix: string }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selProject = useAppSelector(selectProject);

  const annex = String(selProject?.data?.data?.input?.ec?.nationalAnnex ?? "");
  if (annex !== "ANNEX_DENMARK") {
    return null;
  }

  const inspectionLevelOptions: Array<NGDropdownListItem<string>> = [
    { id: "INSPECTION_LEVEL_RELAXED", name: t("relaxed_.label", "Relaxed") },
    { id: "INSPECTION_LEVEL_NORMAL", name: t("normal_.label", "Normal") },
    { id: "INSPECTION_LEVEL_TIGHTENED", name: t("tightened_.label", "Tightened") },
  ];

  const element = selProject?.data?.data?.input?.structures?.[0]?.elements?.[0];
  const inspectionLevelId = String(element?.inspectionLevel ?? "INSPECTION_LEVEL_NORMAL");
  const critical = Boolean(element?.critical);
  const inspectionLevel =
    inspectionLevelOptions.find((option) => option.id === inspectionLevelId) ?? inspectionLevelOptions[1];

  const updateElement = (updater: (element: Record<string, unknown>) => void) => {
    if (!selProject) {
      return;
    }

    const nextProject = structuredClone(selProject);
    const nextElement = nextProject?.data?.data?.input?.structures?.[0]?.elements?.[0] as
      | Record<string, unknown>
      | undefined;

    if (!nextElement) {
      return;
    }

    updater(nextElement);
    dispatch(ProjectSlice.actions.applyProjectEdit(nextProject));
    // Both settings scale the Danish NA partial factors shown in the coefficient tabs next to this
    // panel, so recompute them via the eurocode gamma endpoint.
    dispatch(updateGammaApi());
  };

  return (
    <>
      <Row className="mb-3">
        <Col xs={12} className="editfield-label">
          <span>{t(labelKey, "Inspection level")}</span>
        </Col>
        <Col xs={12} className="ps-0 px-0">
          <SXDropdown
            name={"inspection_level_" + idPrefix}
            id={"inspection-level-" + idPrefix + "-select"}
            list={inspectionLevelOptions}
            val={inspectionLevel}
            dropDirection="down"
            onUpdate={(value) => {
              if (!value) {
                return;
              }
              updateElement((element) => {
                element.inspectionLevel = value.id;
              });
            }}
            notSelectedName={t("not_set_.label", "Not set")}
            localisation={false}
            rawNames={true}
            lang=""
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs={12} className="ps-0 px-0 d-flex align-items-center justify-content-between" style={{ height: 32 }}>
          <Form.Check
            type="switch"
            id={"critical-element-" + idPrefix + "-switch"}
            label={t("critical_.label", "Critical element")}
            className="sx-switch mb-0"
            checked={critical}
            onChange={(e) => {
              const checked = e.target.checked;
              updateElement((element) => {
                element.critical = checked;
              });
            }}
          />
        </Col>
      </Row>
    </>
  );
};

const SteelSettingsContent = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selProject = useAppSelector(selectProject);

  const steelLtMethodOptions: Array<NGDropdownListItem<string>> = [
    { id: "LATERAL_TORSIONAL_METHOD_GENERAL", name: t("general_.label", "General") },
    {
      id: "LATERAL_TORSIONAL_METHOD_GENERAL_SPEC_FOR_I",
      name: t("general_special_for_I_.label", "General special for I"),
    },
    { id: "LATERAL_TORSIONAL_METHOD_SIMPLIFIED", name: t("simplified_.label", "Simplified") },
  ];

  const steelBucklingCurveOptions: Array<NGDropdownListItem<string>> = [
    { id: "BUCKLING_CURVE_FLEXURAL_AUTO", name: t("auto_.label", "Auto") },
    { id: "BUCKLING_CURVE_FLEXURAL_A0", name: "A0" },
    { id: "BUCKLING_CURVE_FLEXURAL_A", name: "A" },
    { id: "BUCKLING_CURVE_FLEXURAL_B", name: "B" },
    { id: "BUCKLING_CURVE_FLEXURAL_C", name: "C" },
    { id: "BUCKLING_CURVE_FLEXURAL_D", name: "D" },
  ];

  const steelLtCurveOptions: Array<NGDropdownListItem<string>> = [
    { id: "BUCKLING_CURVE_LATERAL_AUTO", name: t("auto_.label", "Auto") },
    { id: "BUCKLING_CURVE_LATERAL_A", name: "A" },
    { id: "BUCKLING_CURVE_LATERAL_B", name: "B" },
    { id: "BUCKLING_CURVE_LATERAL_C", name: "C" },
    { id: "BUCKLING_CURVE_LATERAL_D", name: "D" },
  ];

  const steelBeam = selProject?.data?.data?.input?.structures?.[0]?.elements?.[0]?.designSettings?.steel?.beam as
    | {
      class4NotAllowed?: boolean;
      lateralTorsionalMethod?: string;
      checkResistanceOnly?: boolean;
      fbCurveStiff?: string;
      fbCurveWeak?: string;
      ftCurve?: string;
      ltbCurveTop?: string;
      ltbCurveBtm?: string;
    }
    | undefined;

  const class4NotAllowed = Boolean(steelBeam?.class4NotAllowed);
  const checkResistanceOnly = Boolean(steelBeam?.checkResistanceOnly);

  const lateralTorsionalMethodId = String(steelBeam?.lateralTorsionalMethod ?? "LATERAL_TORSIONAL_METHOD_GENERAL");
  const fbCurveStiffId = String(steelBeam?.fbCurveStiff ?? "BUCKLING_CURVE_FLEXURAL_AUTO");
  const fbCurveWeakId = String(steelBeam?.fbCurveWeak ?? "BUCKLING_CURVE_FLEXURAL_AUTO");
  const ftCurveId = String(steelBeam?.ftCurve ?? "BUCKLING_CURVE_FLEXURAL_AUTO");
  const ltbCurveTopId = String(steelBeam?.ltbCurveTop ?? "BUCKLING_CURVE_LATERAL_AUTO");
  const ltbCurveBtmId = String(steelBeam?.ltbCurveBtm ?? "BUCKLING_CURVE_LATERAL_AUTO");

  const lateralTorsionalMethod =
    steelLtMethodOptions.find((option) => option.id === lateralTorsionalMethodId) ?? steelLtMethodOptions[0];
  const fbCurveStiff =
    steelBucklingCurveOptions.find((option) => option.id === fbCurveStiffId) ?? steelBucklingCurveOptions[0];
  const fbCurveWeak =
    steelBucklingCurveOptions.find((option) => option.id === fbCurveWeakId) ?? steelBucklingCurveOptions[0];
  const ftCurve = steelBucklingCurveOptions.find((option) => option.id === ftCurveId) ?? steelBucklingCurveOptions[0];
  const ltbCurveTop = steelLtCurveOptions.find((option) => option.id === ltbCurveTopId) ?? steelLtCurveOptions[0];
  const ltbCurveBtm = steelLtCurveOptions.find((option) => option.id === ltbCurveBtmId) ?? steelLtCurveOptions[0];

  const updateSteelElement = (updater: (element: Record<string, unknown>) => void) => {
    if (!selProject) {
      return;
    }

    const nextProject = structuredClone(selProject);
    const element = nextProject?.data?.data?.input?.structures?.[0]?.elements?.[0] as
      | Record<string, unknown>
      | undefined;

    if (!element) {
      return;
    }

    updater(element);
    dispatch(ProjectSlice.actions.applyProjectEdit(nextProject));
  };

  const updateSteelBeam = (updater: (beam: Record<string, unknown>) => void) => {
    updateSteelElement((element) => {
      element.designSettings ??= {};
      const designSettings = element.designSettings as Record<string, unknown>;

      if (!designSettings.steel || typeof designSettings.steel !== "object") {
        designSettings.steel = {};
      }
      const steel = designSettings.steel as Record<string, unknown>;

      if (!steel.beam || typeof steel.beam !== "object") {
        steel.beam = {};
      }
      const beam = steel.beam as Record<string, unknown>;
      updater(beam);
    });
  };

  return (
    <Container fluid className="px-3">
      <Row className="ps-0 mb-3">
        <Col xs={12} className="ps-0 px-0 border-bottom">
          <span className="header pb-2">{t("element_.label", "Element")}</span>
        </Col>
      </Row>

      <DanishElementSettings labelKey="inspection_level_steel.label" idPrefix="steel" />

      <Row className="mb-3">
        <Col xs={12} className="ps-0 px-0 d-flex align-items-center justify-content-between" style={{ height: 32 }}>
          <Form.Check
            type="switch"
            label={t("class_4_not_allowed.label", "Class 4 not allowed")}
            className="sx-switch mb-0"
            checked={class4NotAllowed}
            onChange={(e) => {
              const checked = e.target.checked;
              updateSteelBeam((beam) => {
                beam.class4NotAllowed = checked;
              });
            }}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs={12} className="editfield-label">
          <span>{t("lateral_torsional_method_.label", "Lateral torsional method")}</span>
        </Col>
        <Col xs={12} className="ps-0 px-0">
          <SXDropdown
            name="lateral_torsional_method"
            id="lateral-torsional-method-select"
            list={steelLtMethodOptions}
            val={lateralTorsionalMethod}
            dropDirection="down"
            onUpdate={(value) => {
              if (!value) {
                return;
              }
              updateSteelBeam((beam) => {
                beam.lateralTorsionalMethod = value.id;
              });
            }}
            notSelectedName="Select method"
            localisation={false}
            rawNames={true}
            lang=""
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs={12} className="ps-0 px-0 d-flex align-items-center justify-content-between" style={{ height: 32 }}>
          <Form.Check
            type="switch"
            label={t("check_resistance_only_.label", "Check resistance only")}
            className="sx-switch mb-0"
            checked={checkResistanceOnly}
            onChange={(e) => {
              const checked = e.target.checked;
              updateSteelBeam((beam) => {
                beam.checkResistanceOnly = checked;
              });
            }}
          />
        </Col>
      </Row>

      <Row className="ps-0 mb-3">
        <Col xs={12} className="ps-0 px-0 border-bottom">
          <span className="header pb-2">{t("flexural_buckling_curve_.label", "Flexural buckling curve")}</span>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs={12} className="editfield-label">
          <span>{t("stiff_direction_.label", "Stiff direction")}</span>
        </Col>
        <Col xs={12} className="ps-0 px-0">
          <SXDropdown
            name="fb_curve_stiff"
            id="fb-curve-stiff-select"
            list={steelBucklingCurveOptions}
            val={fbCurveStiff}
            dropDirection="down"
            onUpdate={(value) => {
              if (!value) {
                return;
              }
              updateSteelBeam((beam) => {
                beam.fbCurveStiff = value.id;
              });
            }}
            notSelectedName="Select curve"
            localisation={false}
            rawNames={true}
            lang=""
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs={12} className="editfield-label">
          <span>{t("weak_direction_.label", "Weak direction")}</span>
        </Col>
        <Col xs={12} className="ps-0 px-0">
          <SXDropdown
            name="fb_curve_weak"
            id="fb-curve-weak-select"
            list={steelBucklingCurveOptions}
            val={fbCurveWeak}
            dropDirection="down"
            onUpdate={(value) => {
              if (!value) {
                return;
              }
              updateSteelBeam((beam) => {
                beam.fbCurveWeak = value.id;
              });
            }}
            notSelectedName="Select curve"
            localisation={false}
            rawNames={true}
            lang=""
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs={12} className="editfield-label">
          <span>{t("torsional_.label", "Torsional")}</span>
        </Col>
        <Col xs={12} className="ps-0 px-0">
          <SXDropdown
            name="ft_curve"
            id="ft-curve-select"
            list={steelBucklingCurveOptions}
            val={ftCurve}
            dropDirection="down"
            onUpdate={(value) => {
              if (!value) {
                return;
              }
              updateSteelBeam((beam) => {
                beam.ftCurve = value.id;
              });
            }}
            notSelectedName="Select curve"
            localisation={false}
            rawNames={true}
            lang=""
          />
        </Col>
      </Row>

      <Row className="ps-0 mb-3">
        <Col xs={12} className="ps-0 px-0 border-bottom">
          <span className="header pb-2">{t("lateral_buckling_curve_.label", "Lateral buckling curve")}</span>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs={12} className="editfield-label">
          <span>{t("top_flange_.label", "Top flange")}</span>
        </Col>
        <Col xs={12} className="ps-0 px-0">
          <SXDropdown
            name="ltb_curve_top"
            id="ltb-curve-top-select"
            list={steelLtCurveOptions}
            val={ltbCurveTop}
            dropDirection="down"
            onUpdate={(value) => {
              if (!value) {
                return;
              }
              updateSteelBeam((beam) => {
                beam.ltbCurveTop = value.id;
              });
            }}
            notSelectedName="Select curve"
            localisation={false}
            rawNames={true}
            lang=""
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs={12} className="editfield-label">
          <span>{t("bottom_flange_.label", "Bottom flange")}</span>
        </Col>
        <Col xs={12} className="ps-0 px-0">
          <SXDropdown
            name="ltb_curve_bottom"
            id="ltb-curve-bottom-select"
            list={steelLtCurveOptions}
            val={ltbCurveBtm}
            dropDirection="down"
            onUpdate={(value) => {
              if (!value) {
                return;
              }
              updateSteelBeam((beam) => {
                beam.ltbCurveBtm = value.id;
              });
            }}
            notSelectedName="Select curve"
            localisation={false}
            rawNames={true}
            lang=""
          />
        </Col>
      </Row>
    </Container>
  );
};

const ConcreteSettingsContent = () => {
  const dispatch = useAppDispatch();
  const selProject = useAppSelector(selectProject);
  const t = useTranslation().t;

  const input_: ng.input.Data_json = selProject?.data?.data?.input ?? ({} as ng.input.Data_json);
  const code = input_.ec?.nationalAnnex ?? ng.eurocode.Annex.ANNEX_COMMON;

  const rcDesignSettings = selProject?.data?.data?.input?.structures?.[0]?.elements?.[0]?.designSettings?.rc ?? null;

  const updateRcDesignSettings = (updater: (rc: Record<string, unknown>) => void) => {
    if (!selProject) {
      return;
    }

    const nextProject = structuredClone(selProject);
    const element = nextProject?.data?.data?.input?.structures?.[0]?.elements?.[0];
    if (!element) {
      return;
    }

    element.designSettings ??= {};

    const designSettings = element.designSettings as Record<string, unknown>;
    if (!designSettings.rc) {
      designSettings.rc = {};
    }

    // Element specific rc design settings belong in the `column` member of the `element_type` oneof,
    // never in `beam`: the RC engine tests `has_beam()` first and would then ignore the column settings.
    const rc = designSettings.rc as Record<string, unknown>;
    if (!rc.column || typeof rc.column !== "object") {
      rc.column = {};
    }

    updater(rc);
    dispatch(ProjectSlice.actions.applyProjectEdit(nextProject));
  };

  const fabricationOptions: Array<NGDropdownListItem<string>> = [
    { id: "FABRICATION_IN_SITU", name: t("in_situ_.label", "In-situ") },
    { id: "FABRICATION_PREFAB", name: t("prefab_.label", "Prefab") },
  ];

  const fabrication = String(
    (rcDesignSettings as { fabrication?: string } | null)?.fabrication ?? "FABRICATION_IN_SITU",
  );
  const selectedFabrication = fabricationOptions.find((option) => option.id === fabrication) ?? null;

  const shearDesignType = String(
    (rcDesignSettings as { column?: { shearDesignType?: string } } | null)?.column?.shearDesignType ??
    "SHEAR_DESIGN_TYPE_WITH_SHEAR_REINFORCEMENT",
  );
  const enhancedShear = Boolean((rcDesignSettings as { enhancedShear?: boolean } | null)?.enhancedShear);
  const crackedInShear = Boolean((rcDesignSettings as { crackedInShear?: boolean } | null)?.crackedInShear);
  const cot = Number((rcDesignSettings as { cot?: number } | null)?.cot ?? 2.5);
  const fctmType = String((rcDesignSettings as { fctmType?: string } | null)?.fctmType ?? "FCTM_TYPE_FCTM");
  const useCompReinf = Boolean((rcDesignSettings as { useCompReinf?: boolean } | null)?.useCompReinf);
  const selectedShearDesignType = SHEAR_DESIGN_TYPE_OPTIONS.find((option) => option.id === shearDesignType) ?? null;

  const FCTM_TYPE_OPTIONS: Array<{ value: string; label: string }> = code === "ANNEX_SWEDEN" ?
    [
      { value: "FCTM_TYPE_FCTM", label: "fctm" },
      { value: "FCTM_TYPE_FCTM_FL", label: "fctm·fl" },
      { value: "FCTM_TYPE_FCTM_XI", label: "fctm·xi" }
    ] :
    [
      { value: "FCTM_TYPE_FCTM", label: "fctm" },
      { value: "FCTM_TYPE_FCTM_FL", label: "fctm·fl" },
    ];

  const FCTM_TYPE_DROPDOWN_OPTIONS: Array<NGDropdownListItem<string>> = FCTM_TYPE_OPTIONS.map((option) => ({
    id: option.value,
    name: option.label,
  }));


  const selectedFctmType = FCTM_TYPE_DROPDOWN_OPTIONS.find((option) => option.id === fctmType) ?? null;

  return (
    <Container fluid className="px-3">
      {/* SECTION */}
      <Row className="mb-3">
        <Col>
          <h6 className="border-bottom pb-2">{t("element_.label", "Element")}</h6>
        </Col>
      </Row>

      <DanishElementSettings labelKey="inspection_level_concrete.label" idPrefix="concrete" />

      {/* ROW */}
      <Row className="mb-3">
        <Col xs={12} className="editfield-label">
          <span>{t("shear_design_type_.label", "Shear design type")}</span>
        </Col>
        <Col xs={12} className="ps-0 px-0">
          <SXDropdown
            name="shear_design_type"
            id="shear-design-type-select"
            list={SHEAR_DESIGN_TYPE_OPTIONS}
            val={selectedShearDesignType}
            dropDirection="down"
            onUpdate={(value) => {
              if (!value) {
                return;
              }
              updateRcDesignSettings((rc) => {
                const column = rc.column as Record<string, unknown>;
                column.shearDesignType = value.id;
              });
            }}
            notSelectedName="Select shear design type"
            localisation={false}
            rawNames={true}
            lang=""
          />
        </Col>
      </Row>

      {/* ROW */}
      <Row className="mb-3">
        <Col xs={12} className="editfield-label">
          <span>{t("fabrication_.label", "Fabrication")}</span>
        </Col>
        <Col xs={12} className="ps-0 px-0">
          <SXDropdown
            name="fabrication"
            id="fabrication-select"
            list={fabricationOptions}
            val={selectedFabrication}
            dropDirection="down"
            onUpdate={(value) => {
              if (!value) {
                return;
              }
              updateRcDesignSettings((rc) => {
                rc.fabrication = value.id;
              });
              // EN 1992-1-1 Table 2.1a reduces the concrete and reinforcement partial factors for
              // prefabricated elements, so refresh the coefficients shown next to this panel.
              dispatch(updateGammaApi());
            }}
            notSelectedName={t("not_set_.label", "Not set")}
            localisation={false}
            rawNames={true}
            lang=""
          />
        </Col>
      </Row>

      {/* ROW */}
      <Row className="mb-3">
        <Col xs={12} className="ps-0 px-0 d-flex align-items-center justify-content-between" style={{ height: 32 }}>
          <Form.Check
            type="switch"
            label={t("reduced_shear_force_close_to_supports_.label", "Reduced shear force close to supports")}
            className="sx-switch mb-0"
            checked={enhancedShear}
            onChange={(e) => {
              const checked = e.target.checked;
              updateRcDesignSettings((rc) => {
                rc.enhancedShear = checked;
              });
            }}
          />
        </Col>
      </Row>

      {/* ROW */}
      <Row className="mb-3">
        <Col xs={12} className="ps-0 px-0 d-flex align-items-center justify-content-between" style={{ height: 32 }}>
          <Form.Check
            type="switch"
            label={t("cracked_in_shear_.label", "Cracked in shear")}
            className="sx-switch mb-0"
            checked={crackedInShear}
            onChange={(e) => {
              const checked = e.target.checked;
              updateRcDesignSettings((rc) => {
                rc.crackedInShear = checked;
              });
            }}
          />
        </Col>
      </Row>

      {/* ROW */}
      <Row className="mb-3">
        <Col xs={12} className="editfield-label">
          <span>Cot (&theta;) </span>
        </Col>

        <Col xs={12} className="ps-0 px-0" style={{ height: 32 }}>
          <SXEditField
            fullWidth
            textAlign="left"
            fontSize={12}
            type="number"
            disableNegativeNumber
            val={cot}
            onUpdate={(newValue: number) => {
              const nextCot = Number(newValue);
              updateRcDesignSettings((rc) => {
                rc.cot = Number.isFinite(nextCot) ? nextCot : 2.5;
              });
            }}
          />
        </Col>
      </Row>

      {/* ROW */}
      <Row className="mb-3">
        <Col xs={12} className="editfield-label">
          <span>{t("fctm_type_.label", "Tensile strength")}</span>
        </Col>

        <Col xs={12} className="ps-0 px-0">
          <SXDropdown
            name="fctm_type"
            id="fctm-type-select"
            list={FCTM_TYPE_DROPDOWN_OPTIONS}
            val={selectedFctmType}
            dropDirection="down"
            onUpdate={(value) => {
              if (!value) {
                return;
              }
              updateRcDesignSettings((rc) => {
                rc.fctmType = value.id;
              });
            }}
            notSelectedName="Select tensile strength"
            localisation={false}
            rawNames={true}
            lang=""
          />
        </Col>
      </Row>

      {/* ROW */}
      <Row className="mb-3">
        <Col xs={12} className="ps-0 px-0 d-flex align-items-center justify-content-between" style={{ height: 32 }}>
          <Form.Check
            type="switch"
            label={t("use_compressed_reinforcement_.label", "Use compressed reinforcement")}
            className="sx-switch mb-0"
            checked={useCompReinf}
            onChange={(e) => {
              const checked = e.target.checked;
              updateRcDesignSettings((rc) => {
                rc.useCompReinf = checked;
              });
            }}
          />
        </Col>
      </Row>
    </Container>
  );
};

export const MaterialSettingsModal = (props: MaterialSettingsModalProps) => {

  const t = useTranslation().t;
  const selProject = useAppSelector(selectProject);
  const isConcrete = props.selectedGroup === "Concrete";
  const isTimber = props.selectedGroup === "Timber";
  const isDenmarkAnnex = String(selProject?.data?.data?.input?.ec?.nationalAnnex ?? "") === "ANNEX_DENMARK";
  const modalTitles: Record<"Concrete" | "Steel" | "Timber", string> = {
    Concrete: `${t("concrete_.label", "Concrete")} ${t("settings_.label", "settings")}`,
    Steel: `${t("steel_.label", "Steel")} ${t("settings_.label", "settings")}`,
    Timber: `${t("timber_.label", "Timber")} ${t("settings_.label", "settings")}`,
  };

  return (
    <SXModalBase
      size={isTimber && !isDenmarkAnnex ? "lg" : "xl"}
      header={modalTitles[props.selectedGroup]}
      show={props.show}
      close={props.close}
      body={
        <Container fluid>
          {isConcrete && (
            <Row>
              <Col xs={12} lg={6}>
                <ConcreteSettingsContent />
              </Col>
              <Col xs={12} lg={6}>
                <PartialCoefficientsTabs />
              </Col>
            </Row>
          )}

          {isTimber && (
            <Row>
              {/* Timber has no other element settings of its own, so the column only exists for the
                  Danish NA inspection level / critical element. */}
              {isDenmarkAnnex && (
                <Col xs={12} lg={6}>
                  <Container fluid className="px-3">
                    <Row className="mb-3">
                      <Col>
                        <h6 className="border-bottom pb-2">{t("element_.label", "Element")}</h6>
                      </Col>
                    </Row>

                    <DanishElementSettings labelKey="inspection_level_timber.label" idPrefix="timber" />
                  </Container>
                </Col>
              )}

              <Col xs={12} lg={isDenmarkAnnex ? 6 : 12} className="ms-lg-auto">
                <TimberPartialCoefficientsTabs />
              </Col>
            </Row>
          )}

          {!isConcrete && !isTimber && (
            <Row>
              <Col xs={12} lg={6}>
                <SteelSettingsContent />
              </Col>
              <Col xs={12} lg={6}>
                <SteelPartialCoefficientsTabs />
              </Col>
            </Row>
          )}
        </Container>
      }
      footer={
        <Button className="primary-btn" onClick={props.close}>
          {t("close_.label", "Close")}
        </Button>
      }
    />
  );
};
